import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from 'components/ui/Icon';
import { throttle } from 'utils/throttle';
import { useTranslation } from 'hooks/useTranslation';
import styles from './Popover.module.scss';
import type { ReactNode } from 'react';

type Coords = {
  left: number;
  top: number;
};

type Props = {
  onClose: () => void;
  open: boolean;
  showCloseIcon?: boolean;
  target: HTMLElement | null;
  container?: HTMLElement;
  placement?: 'bottom' | 'top';
  arrow?: boolean;
  zIndex?: string;
  preventClickOutsideOn?: HTMLElement;
  children: ReactNode;
  className?: string;
};

const Popover = ({
  onClose,
  open,
  showCloseIcon = true,
  target,
  container,
  placement = 'top',
  arrow = true,
  zIndex = 'z-10',
  preventClickOutsideOn,
  children,
  className,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [, setIsOpen] = useState(open);
  const [coords, setCoords] = useState<Coords | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  const handleClose = (): void => {
    setIsOpen(false);
    onClose?.();
  };

  const handleCloseOutside = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (ref.current && !ref.current.contains(target)) {
      if (target !== preventClickOutsideOn) {
        setIsOpen(false);
        onClose?.();
      }
    }
  };

  const updatePopoverCoords = throttle(() => {
    if (target) {
      const rect = target.getBoundingClientRect();

      if (container) {
        const containerRect = container.getBoundingClientRect();
        setCoords({
          left: rect.left + rect.width / 2,
          top: (ref.current?.offsetHeight || 0) + containerRect.bottom,
        });
      } else {
        setCoords({
          left: rect.left + rect.width / 2,
          top: rect.top + window.scrollY,
        });
      }
    }
  }, 500);

  useEffect(() => {
    setIsOpen(open);
    if (target && open) {
      const rect = target.getBoundingClientRect();

      if (container) {
        const containerRect = container.getBoundingClientRect();
        setCoords({
          left: rect.left + rect.width / 2,
          top: (ref.current?.offsetHeight || 0) + containerRect.bottom,
        });
      } else {
        setCoords({
          left: rect.left + rect.width / 2,
          top: rect.top + window.scrollY,
        });
      }
    }
  }, [open]);

  useEffect(() => {
    document.addEventListener('click', handleCloseOutside, true);
    return () =>
      document.removeEventListener('click', handleCloseOutside, true);
  });

  useEffect(() => {
    window.addEventListener('resize', updatePopoverCoords, {
      passive: true,
    });

    return () => window.removeEventListener('resize', updatePopoverCoords);
  }, []);

  return (
    <>
      {createPortal(
        <div
          ref={ref}
          className={clsx(
            'rounded-lg absolute bg-white',
            zIndex,
            styles.popoverContainer,
            placement === 'top'
              ? styles.popoverContainerTop
              : styles.popoverContainerBottom,
            arrow && styles.popoverContainerArrow,
            className,
          )}
          onClick={handleClose}
          style={{ ...coords }}
        >
          <div className="relative">
            {showCloseIcon && (
              <button
                className={clsx(styles.closeBtn, 'right-0 absolute z-50 -m-3')}
                onClick={handleClose}
                title={t('close')}
              >
                <Icon type="Cross" className="w-4 opacity-75" />
              </button>
            )}
            {children}
          </div>
        </div>,
        container || document.body,
      )}
    </>
  );
};

export default Popover;
